import { Plus, Minus, ShoppingCart as ShoppingCartIcon, Package, Building2, BarChart3 } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';

export interface CartItem {
  id: string;
  type: 'venue' | 'sku' | 'retail-media' | 'reporting';
  name: string;
  category?: string;
  image?: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  metadata?: any;
  editable?: boolean; // Whether quantity can be changed in cart
}

interface ShoppingCartProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: CartItem[];
  onQuantityChange?: (itemId: string, newQuantity: number) => void;
  currentStep?: number; // Current step in the tender process
}

export function ShoppingCart({ open, onOpenChange, items, onQuantityChange, currentStep = 1 }: ShoppingCartProps) {
  // Helper function to format numbers with commas
  const formatNumber = (num: number): string => {
    return num.toLocaleString('en-US');
  };

  // Helper function to format currency with commas
  const formatCurrency = (num: number): string => {
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Filter out venue items - they don't need to be displayed in the cart
  // Also filter out retail-media items until step 3 (Retail Media step)
  const displayItems = items.filter(item => {
    if (item.type === 'venue') return false;
    if (item.type === 'retail-media' && currentStep < 3) return false;
    return true;
  });
  
  const subtotal = displayItems.reduce((sum, item) => sum + item.totalPrice, 0);
  const setupFees = items
    .filter(item => item.type === 'retail-media' && item.metadata?.setupPrice)
    .reduce((sum, item) => {
      const setupPrice = item.metadata?.setupPrice || 0;
      // Setup price is charged once per item type, regardless of machines/units
      return sum + setupPrice;
    }, 0);
  const shippingFee = 0; // No shipping fee for digital services
  const total = subtotal + setupFees + shippingFee;

  const handleQuantityChange = (itemId: string, delta: number) => {
    if (!onQuantityChange) return;
    const item = items.find(i => i.id === itemId);
    if (!item) return;
    const newQuantity = Math.max(1, item.quantity + delta);
    onQuantityChange(itemId, newQuantity);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg flex flex-col p-0">
        <SheetHeader className="border-b pb-4 px-6 pt-6">
          <SheetTitle className="text-xl font-semibold">Shopping Cart</SheetTitle>
        </SheetHeader>

        <div className="flex flex-col flex-1 min-h-0">
          {/* Cart Items - Scrollable */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {displayItems.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p>Your cart is empty</p>
                <p className="text-sm mt-2">Add items from the steps above</p>
              </div>
            ) : (
              displayItems.map((item) => (
                <div key={item.id} className="flex gap-3 pb-4 border-b last:border-0">
                  {/* Image */}
                  <div className="relative flex-shrink-0">
                    {item.image ? (
                      <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23f1f5f9" width="100" height="100"/%3E%3Ctext x="50%25" y="50%25" font-family="Arial" font-size="12" fill="%2394a3b8" text-anchor="middle" dy=".3em"%3ENo Image%3C/text%3E%3C/svg%3E';
                          }}
                        />
                      </div>
                    ) : (
                      <div className="w-20 h-20 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center">
                        {item.type === 'venue' && <Building2 className="w-10 h-10 text-gray-400" />}
                        {item.type === 'sku' && <Package className="w-10 h-10 text-gray-400" />}
                        {item.type === 'retail-media' && <ShoppingCartIcon className="w-10 h-10 text-gray-400" />}
                        {item.type === 'reporting' && <BarChart3 className="w-10 h-10 text-gray-400" />}
                      </div>
                    )}
                    {item.quantity > 0 && (
                      <div className="absolute -top-2 -right-2 w-5 h-5 bg-blue-600 text-white text-xs font-semibold rounded-full flex items-center justify-center">
                        {item.type === 'sku' && item.metadata?.uniqueSKUPositions ? formatNumber(item.metadata.uniqueSKUPositions) : formatNumber(item.quantity)}
                      </div>
                    )}
                  </div>

                  {/* Item Details */}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900">{item.name}</div>
                    {item.category && item.type !== 'sku' && (
                      <div className="text-xs text-gray-500 mt-0.5">{item.category}</div>
                    )}
                    
                    {/* Quantity Selector */}
                    {item.editable && onQuantityChange ? (
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          type="button"
                          onClick={() => handleQuantityChange(item.id, -1)}
                          className="w-7 h-7 rounded border border-gray-300 flex items-center justify-center hover:bg-gray-50 transition-colors"
                          disabled={item.quantity <= 1}
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-8 text-center text-sm font-medium">{formatNumber(item.quantity)}</span>
                        <button
                          type="button"
                          onClick={() => handleQuantityChange(item.id, 1)}
                          className="w-7 h-7 rounded border border-gray-300 flex items-center justify-center hover:bg-gray-50 transition-colors"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <div className="text-xs text-gray-500 mt-1">
                        {item.type === 'venue' 
                          ? 'Total machines' 
                          : item.type === 'sku' 
                            ? item.metadata?.uniqueSKUPositions && item.metadata?.totalMachines
                              ? `${formatNumber(item.metadata.uniqueSKUPositions)} SKU${item.metadata.uniqueSKUPositions !== 1 ? 's' : ''} in ${formatNumber(item.metadata.totalMachines)} machine${item.metadata.totalMachines !== 1 ? 's' : ''}`
                              : 'Machine Distribution'
                            : 'Quantity'}: {item.type === 'sku' ? formatNumber(item.metadata?.uniqueSKUPositions || item.quantity) : formatNumber(item.quantity)}
                      </div>
                    )}

                    {/* Price - Show monthly and total for SKU and Reporting items */}
                    {(item.type === 'sku' || item.type === 'reporting') && item.metadata?.monthlyPrice && item.metadata?.durationMonths ? (
                      <div className="mt-2 space-y-1">
                        <div className="text-sm text-gray-600">
                          Monthly: £{formatCurrency(item.metadata.monthlyPrice)}
                        </div>
                        <div className="text-sm font-semibold text-gray-900">
                          Total ({item.metadata.durationMonths} months): £{formatCurrency(item.totalPrice)}
                        </div>
                      </div>
                    ) : (
                      <div className="mt-2 text-sm font-semibold text-gray-900">
                        £{formatCurrency(item.totalPrice)}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Order Summary - Sticky at Bottom */}
          {displayItems.length > 0 && (
            <div className="sticky bottom-0 bg-white border-t pt-4 pb-6 px-6 space-y-3 shadow-lg">
              <div className="space-y-2 text-sm">
                {/* Calculate monthly total for SKU and Reporting items */}
                {(() => {
                  const monthlyItems = displayItems.filter(item => 
                    (item.type === 'sku' || item.type === 'reporting') && item.metadata?.monthlyPrice
                  );
                  const monthlyTotal = monthlyItems.reduce((sum, item) => {
                    return sum + (item.metadata?.monthlyPrice || 0);
                  }, 0);
                  const twelveMonthTotal = monthlyItems.reduce((sum, item) => {
                    return sum + (item.totalPrice || 0);
                  }, 0);
                  const otherItemsTotal = displayItems
                    .filter(item => item.type !== 'sku' && item.type !== 'reporting')
                    .reduce((sum, item) => sum + item.totalPrice, 0);
                  
                  return (
                    <>
                      {monthlyItems.length > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Monthly Total</span>
                          <span className="font-medium">£{formatCurrency(monthlyTotal)}</span>
                        </div>
                      )}
                      {otherItemsTotal > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Other Items</span>
                          <span className="font-medium">£{formatCurrency(otherItemsTotal)}</span>
                        </div>
                      )}
                      {setupFees > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Setup Fees</span>
                          <span className="font-medium">£{formatCurrency(setupFees)}</span>
                        </div>
                      )}
                      {shippingFee > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Shipping fee</span>
                          <span className="font-medium">£{formatCurrency(shippingFee)}</span>
                        </div>
                      )}
                      <div className="border-t pt-2 flex justify-between">
                        <span className="text-lg font-semibold">Total</span>
                        <span className="text-lg font-bold">£{formatCurrency(twelveMonthTotal + otherItemsTotal + setupFees)}</span>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

